import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";

export interface CompanyRole {
  id: string;
  company_id: string;
  name: string;
  base_level: number;
  color: string;
  is_system_role: boolean;
  description: string | null;
  permissions: Record<string, boolean>;
  created_at: string;
  updated_at: string;
  user_count?: number;
}

export const PERMISSION_GROUPS = [
  {
    label: "Projects",
    keys: [
      { key: "view_projects", label: "View projects" },
      { key: "edit_project_info", label: "Edit project info" },
      { key: "edit_contract_sov", label: "Edit contract / SOV" },
      { key: "create_work_orders", label: "Create work orders" },
      { key: "edit_work_orders", label: "Edit work orders" },
      { key: "view_unit_schedule", label: "View unit schedule" },
      { key: "manage_unit_schedule", label: "Manage unit schedule" },
    ],
  },
  {
    label: "Financial",
    keys: [
      { key: "view_financials", label: "View financial data" },
      { key: "approve_bills", label: "Approve bills (AP)" },
      { key: "approve_invoices", label: "Approve invoices (AR)" },
      { key: "approve_payments", label: "Approve payments" },
      { key: "approve_rate_overrides", label: "Approve sub rate overrides" },
      { key: "edit_billing_cycles", label: "Edit billing cycles" },
      { key: "close_periods", label: "Close/reopen accounting periods" },
      { key: "create_invoices", label: "Create invoices (AR)" },
    ],
  },
  {
    label: "Directory",
    keys: [
      { key: "manage_vendors", label: "Manage vendors" },
      { key: "manage_customers", label: "Manage customers" },
      { key: "view_compliance", label: "View compliance dashboard" },
    ],
  },
  {
    label: "Reports",
    keys: [{ key: "view_reports", label: "View reports" }],
  },
  {
    label: "System",
    keys: [
      { key: "manage_settings", label: "Manage settings" },
      { key: "invite_users", label: "Invite users" },
      { key: "manage_roles", label: "Manage roles" },
    ],
  },
  {
    label: "Project Assignment",
    keys: [
      { key: "view_all_projects", label: "View all projects automatically" },
      { key: "auto_assign_all_projects", label: "Auto-assign to all projects" },
    ],
  },
  {
    label: "Accounts Payable",
    keys: [
      { key: "view_accounts_payable", label: "View accounts payable queue" },
      { key: "schedule_payments", label: "Schedule and record bill payments" },
      { key: "submit_expenses", label: "Submit expense records" },
      { key: "void_bills", label: "Void bills (destructive)" },
      { key: "manage_expenses", label: "Manage project expenses" },
    ],
  },
  {
    label: "Field & Contract Labor",
    keys: [
      { key: "view_assigned_wo_only", label: "Restrict WO visibility to assigned only" },
      { key: "submit_labor_hours", label: "Submit labor hours / timesheets" },
    ],
  },
];

export const useCompanyRoles = () => {
  const { user } = useAuth();
  return useQuery({
    queryKey: ["company_roles", user?.id],
    queryFn: async () => {
      const { data: roles, error } = await supabase
        .from("roles")
        .select("*")
        .order("base_level", { ascending: true });
      if (error) throw error;

      // Get user counts per role
      const { data: users } = await supabase
        .from("user_profiles")
        .select("role_id");

      const counts: Record<string, number> = {};
      (users ?? []).forEach((u: any) => {
        counts[u.role_id] = (counts[u.role_id] || 0) + 1;
      });

      return (roles ?? []).map((r: any) => ({
        ...r,
        permissions: r.permissions || {},
        user_count: counts[r.id] || 0,
      })) as CompanyRole[];
    },
    enabled: !!user,
  });
};

export const useUpdateRole = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: { id: string; name?: string; color?: string; description?: string; permissions?: Record<string, boolean> }) => {
      const { id, name, color, description, permissions } = payload;
      const updatePayload: Record<string, any> = {
        updated_at: new Date().toISOString(),
      };
      if (color !== undefined) updatePayload.color = color;
      if (description !== undefined) updatePayload.description = description;
      if (permissions !== undefined) updatePayload.permissions = permissions;
      if (name !== undefined) updatePayload.name = name;

      const { error } = await supabase
        .from("roles")
        .update(updatePayload)
        .eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["company_roles"] }),
  });
};

export const useCreateRole = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: { company_id: string; name: string; base_level: number; color: string; description?: string; permissions: Record<string, boolean> }) => {
      const { data, error } = await supabase
        .from("roles")
        .insert(payload as any)
        .select()
        .single();
      if (error) throw error;
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["company_roles"] }),
  });
};

export const useDeleteRole = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from("roles").delete().eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["company_roles"] }),
  });
};
